import {
  compact,
  endsWith,
  flatten,
  groupBy,
  isEmpty,
  isEqual,
  keyBy,
  last,
  map,
  reject,
  partition,
  startsWith,
  trim,
  uniqBy,
  flatMap,
  reverse,
  filter,
  find,
  join,
} from "lodash/fp";
import { ulid } from "ulid";

import { PARAGRAPH_DELIMITER } from "@app/analytics/api/models";
import {
  SUPPRESSED_SPEAKER_NAMES,
  UNKNOWN_SPEAKER,
} from "@app/analytics/MeetingReport/constants";
import {
  chunkContentBySearchResults,
  chunkNoteBySearchResults,
} from "@app/analytics/MeetingReport/utils/annotationUtils";
import { isChapter } from "@app/analytics/MeetingReport/utils/dataUtils";
import {
  getChapterDescriptionSearchResultKey,
  getChapterTitleSearchResultKey,
} from "@app/analytics/MeetingReport/utils/searchUtils";
import type {
  ApiClipOperation,
  ApiCreateClipOperation,
  ApiCreateSessionNoteOperation,
  ApiCreateTranscriptTurnOperation,
  ApiDeleteClipOperation,
  ApiDeleteSessionNoteOperation,
  ApiDeleteTurnOperation,
  ApiSessionNoteOperation,
  ApiTranscriptOperation,
  ApiTranscriptOperationWord,
  ApiTranscriptWord,
  ApiUpdateClipOperation,
  ApiUpdateSessionNoteOperation,
  ApiUpdateTranscriptTurnOperation,
  BaseTranscriptTurnParagraph,
  CreatableClipType,
  TranscriptTextChunk,
  TranscriptTurnParagraph,
  TranscriptWord,
  WordDelimiter,
} from "@app/common/types";
import {
  insertAt,
  insertManyAt,
  removeManyAt,
  replaceAt,
  replaceWhere,
} from "@app/utils/arrays";

import type {
  BatchEdit,
  Edit,
  NoteEdit,
  NoteItemOrChapter,
  NoteItemOrChapterType,
  NoteSearchResult,
  NoteSearchResultMap,
  SessionChapterEdit,
  SessionNoteEdit,
  SingleEdit,
  SingleNoteEdit,
  SingleSessionNoteEdit,
  SingleSummaryItemEdit,
  SingleTranscriptEdit,
  TranscriptEdit,
  TranscriptSearchResult,
} from "../types";

export const TEMP_ID_PREFIX = "temp-id";
export const getTemporaryId = () => `${TEMP_ID_PREFIX}-${ulid()}`;
export const isNewItem = (item: { id: string }) =>
  startsWith(TEMP_ID_PREFIX, item.id);
export const isNewParagraph = (paragraph: TranscriptTurnParagraph) => {
  const originalTurnParagraphIds = map("id", paragraph.turn.paragraphs);
  return !originalTurnParagraphIds.includes(paragraph.id);
};

export const isBatchedEdit = (edit: Edit): edit is BatchEdit => "edits" in edit;
export const isTurnEdit = (edit: Edit): edit is SingleTranscriptEdit =>
  !isBatchedEdit(edit) && edit.type === "turn";
export const isChapterEdit = (edit: Edit): edit is SessionChapterEdit =>
  !isBatchedEdit(edit) && edit.type === "chapter";
export const isSessionNoteEdit = (edit: NoteEdit): edit is SessionNoteEdit =>
  !isBatchedEdit(edit) && edit.type === "note";
export const isSummaryItemEdit = (
  edit: NoteEdit
): edit is SingleSummaryItemEdit =>
  !isBatchedEdit(edit) &&
  !isTurnEdit(edit) &&
  !isChapterEdit(edit) &&
  !isSessionNoteEdit(edit);

export const combineSingleEdits = <T extends SingleEdit>(
  editA: T,
  editB: T
): T => {
  if (editA.type != editB.type) {
    throw new Error("cannot combine edits with different types");
  }

  if (editA.after.id !== editB.after.id) {
    throw new Error("cannot combine edits with different ids");
  }

  return {
    type: editA.type,
    before: editA.before,
    after: editB.after,
  } as T;
};

export const combineTranscriptEdits = (
  editA: TranscriptEdit,
  editB: TranscriptEdit
): TranscriptEdit => {
  const edits = isBatchedEdit(editA) ? editA.edits : [editA];

  if (isBatchedEdit(editB)) {
    return { edits: [...edits, ...editB.edits] };
  }

  const [createEdits, otherEdits] = partition((e) => !e.before, edits);
  const createEdit = createEdits[0];
  if (!createEdit || createEdit.after.id !== editB.after.id) {
    return { edits: [...edits, editB] };
  }

  const combinedEdit: SingleTranscriptEdit = {
    ...createEdit,
    after: editB.after,
  };

  return { edits: [combinedEdit, ...otherEdits] };
};

const makeIdFromTemporaryId = (id?: string) => {
  if (!id || !startsWith(TEMP_ID_PREFIX, id)) return id;
  return id.replace(/temp-id-/, "");
};

const combineAndSortParagraphs = (
  paragraphs: BaseTranscriptTurnParagraph[]
) => {
  const uniqParagraphs = uniqBy("id", paragraphs);
  const [firstParagraph, rest] = partition(
    (p) => !p.previousParagraphId,
    uniqParagraphs
  );
  const paragraphsByPreviousParagraphId = keyBy("previousParagraphId", rest);
  const sortedParagraphs = [firstParagraph[0]];
  while (sortedParagraphs.length < uniqParagraphs.length) {
    const currentParagraph = last(sortedParagraphs)!;
    const nextParagraph = paragraphsByPreviousParagraphId[currentParagraph.id];
    sortedParagraphs.push(nextParagraph);
  }
  return sortedParagraphs;
};

const getTurnTextAndWords = (
  paragraphs: BaseTranscriptTurnParagraph[],
  wordDelimiter: WordDelimiter
) => {
  const combinedParagraphWords: TranscriptWord[][] = paragraphs.map(
    (paragraph, i) => {
      if (isEmpty(paragraph.words)) return paragraph.words;

      const lastWord = last(paragraph.words)!;
      const shouldApplyDelimiter =
        i < paragraphs.length - 1 &&
        !endsWith(PARAGRAPH_DELIMITER, lastWord.value);
      if (!shouldApplyDelimiter) return paragraph.words;

      const allButLastWord = paragraph.words.slice(
        0,
        paragraph.words.length - 1
      );
      return [
        ...allButLastWord,
        { ...lastWord, value: `${lastWord.value}${PARAGRAPH_DELIMITER}` },
      ];
    }
  );

  const combinedWords = flatten(combinedParagraphWords);
  return {
    words: combinedWords,
    text: map("value", combinedWords).join(wordDelimiter),
  };
};

const makeInitialTurnParagraph = (
  paragraph: TranscriptTurnParagraph,
  wordDelimiter: WordDelimiter
) => {
  const { text, words } = getTurnTextAndWords(
    paragraph.turn.paragraphs,
    wordDelimiter
  );
  return {
    ...paragraph,
    text,
    words,
  };
};

/**
 * @param beforeTurnEdits Edits where the "before" value is in this turn
 * @param afterTurnEdits Edits where the "after" value is in this turn
 * @return edit | null Single edit that is the combination of all the edits
 *              for this turn (across all paragraphs)
 * */
export const combineTurnEdits = (
  beforeTurnEdits: SingleTranscriptEdit[],
  afterTurnEdits: SingleTranscriptEdit[],
  wordDelimiter: WordDelimiter
): SingleTranscriptEdit | null => {
  if (isEmpty(beforeTurnEdits) && isEmpty(afterTurnEdits)) return null;

  // original turn/paragraphs will be the same for all the edits, so just grab the oldest one
  const originalTurn = last(afterTurnEdits)!.after.turn;
  const originalTurnParagraphs = originalTurn.paragraphs ?? [];
  const originalTurnParagraphIds = map("id", originalTurnParagraphs);
  const editsByTurnParagraphId = groupBy(
    (edit) => edit.after.id,
    afterTurnEdits
  );

  // any paragraphs that were previously part of this turn but have since been
  // moved to a different turn need to be stripped out of this turn
  const paragraphIdsThatWereMovedToADifferentTurn = map(
    "after.id",
    filter((edit) => edit.after.turn.id !== originalTurn.id, beforeTurnEdits)
  );

  const originalTurnParagraphsWithTransferredParagraphsRemoved = isEmpty(
    paragraphIdsThatWereMovedToADifferentTurn
  )
    ? originalTurnParagraphs
    : reject(
        (p) => paragraphIdsThatWereMovedToADifferentTurn.includes(p.id),
        originalTurnParagraphs
      );

  const editedParagraphs = compact(
    Object.values(editsByTurnParagraphId).map((edits) => {
      const paragraph = edits[0].after;
      if (originalTurnParagraphIds.includes(paragraph.id)) {
        return paragraph;
      }
      // don't include any paragraphs that were created and deleted during the
      // same editing session
      return paragraph.isDeleted ? null : paragraph;
    })
  );

  // if no paragraphs were edited or moved to a different turn, don't do anything
  const turnHasNoChanges =
    isEmpty(editedParagraphs) &&
    originalTurnParagraphsWithTransferredParagraphsRemoved.length ===
      originalTurnParagraphs.length;

  if (turnHasNoChanges) return null;

  let updatedTurnParagraphs = combineAndSortParagraphs([
    ...editedParagraphs,
    ...originalTurnParagraphsWithTransferredParagraphsRemoved,
  ]);

  // now that we've combined all the paragraphs into a master list, remove the
  // paragraphs that were deleted
  updatedTurnParagraphs = reject("isDeleted", updatedTurnParagraphs);

  // use the first paragraph edit we can find that has a before
  const dummyParagraphEdit =
    find((e) => Boolean(e.before), afterTurnEdits) ?? afterTurnEdits[0];
  const populatedBeforeParagraph = makeInitialTurnParagraph(
    dummyParagraphEdit.before ?? dummyParagraphEdit.after,
    wordDelimiter
  );

  if (isEmpty(updatedTurnParagraphs)) {
    const combinedEdit: TranscriptEdit = {
      type: "turn",
      before: populatedBeforeParagraph,
      after: {
        ...populatedBeforeParagraph,
        isDeleted: true,
      },
    };

    return combinedEdit;
  }

  const { words, text } = getTurnTextAndWords(
    updatedTurnParagraphs,
    wordDelimiter
  );

  const combinedParagraph: TranscriptTurnParagraph = {
    ...dummyParagraphEdit.after,
    words,
    text,
    turn: originalTurn,
    isDeleted: false,
  };

  const combinedEdit: TranscriptEdit = {
    type: "turn",
    before: isEmpty(originalTurnParagraphs)
      ? undefined
      : populatedBeforeParagraph,
    after: combinedParagraph,
  };

  return combinedEdit;
};

/**
 * This method handles condensing many edits to a single paragraph into a single
 * edit, since we don't really care about anything that happened in between the
 * initial state and the most recent edit, as all changes will be encompassed in
 * the `after` of the most recent edit anyway
 * */
const combineTurnParagraphEdits = (
  edits: TranscriptEdit[]
): SingleTranscriptEdit[] => {
  const flattenedEdits = flatMap(
    (edit) => (isBatchedEdit(edit) ? edit.edits : edit),
    edits
  );

  const editsByParagraphId = groupBy("after.id", flattenedEdits);

  return Object.values(editsByParagraphId).map((editsForParagraph) => {
    const mostRecentEdit = editsForParagraph[0];

    // if only one edit, there's nothing to condense
    if (editsForParagraph.length === 1) return mostRecentEdit;

    const oldestEdit = editsForParagraph[editsForParagraph.length - 1];

    return {
      type: "turn",
      before: oldestEdit.before,
      after: mostRecentEdit.after,
    };
  });
};

export const condenseTranscriptEdits = (
  edits: TranscriptEdit[],
  wordDelimiter: WordDelimiter
): SingleTranscriptEdit[] => {
  const dedupedEdits = combineTurnParagraphEdits(edits);

  const editsByBeforeTurnId = groupBy(
    (e) => e.before?.turn.id ?? "no_before_turn",
    dedupedEdits
  );
  const editsByAfterTurnId = groupBy((e) => e.after.turn.id, dedupedEdits);

  const condensedEdits = Object.entries(editsByAfterTurnId).map(
    ([turnId, turnEdits]) => {
      if (isEmpty(turnEdits)) return null;
      return combineTurnEdits(
        editsByBeforeTurnId[turnId],
        turnEdits,
        wordDelimiter
      );
    }
  );

  return compact(condensedEdits);
};

const condenseNoteEditsOfType = (edits: NoteEdit[]): SingleNoteEdit[] => {
  const flattenedEdits = flatMap(
    (edit) => (isBatchedEdit(edit) ? edit.edits : edit),
    edits
  );
  const editsById = groupBy((e) => e.after.id, flattenedEdits);

  const condensedEdits = Object.values(editsById).map((itemEdits) => {
    const mostRecentEdit = itemEdits[0];

    if (itemEdits.length === 1) {
      // short-circuit if there's nothing to condense
      return mostRecentEdit;
    }

    const firstEdit = itemEdits[itemEdits.length - 1];

    // chapter edits will always have a before, but check the type just in
    // case/to appease the types below
    const itemWasCreatedThisSession =
      mostRecentEdit.type !== "chapter" && !firstEdit.before;

    // if this item was created and deleted within the same editing session,
    // just throw away all the edits
    const isDeletedNewItem =
      itemWasCreatedThisSession && Boolean(mostRecentEdit.after.isDeleted);
    if (isDeletedNewItem) return null;

    const isNoopEdit = isEqual(firstEdit.before, mostRecentEdit.after);
    if (isNoopEdit) return null;

    return {
      type: mostRecentEdit.type,
      // if the item was created during this editing session, ensure the
      // "before" is undefined so the APi operation generation will identify
      // it as a new item
      before: itemWasCreatedThisSession ? undefined : firstEdit.before,
      // the "after" value of the most recent edit should always be the most
      // up-to-date version of the edited model (i.e., will already contain
      // all the previous edits)
      after: mostRecentEdit.after,
    } as SingleNoteEdit;
    // ^ have to typecast because otherwise the types barf due to chapter edits
    // requiring a "before" value
  });

  return compact(condensedEdits);
};

export const condenseNoteEdits = (edits: NoteEdit[]): SingleNoteEdit[] => {
  const editsByType = groupBy("type", edits);
  return flatten(map(condenseNoteEditsOfType, Object.values(editsByType)));
};

/**
 * Accepts an array of words and updated text, and returns a new array of words in the same
 * order, with added, updated, deleted and original words as appropriate.
 */
export const getEditedWords = (
  words: TranscriptWord[],
  updatedText: string,
  wordDelimiter: WordDelimiter
): TranscriptWord[] => {
  const editedWords = compact(trim(updatedText).split(wordDelimiter).map(trim));

  const wordsAdded = editedWords.length - words.length;
  const wordsRemoved = words.length - editedWords.length;

  const loopLength = Math.max(words.length, editedWords.length);

  for (let i = 0; i < loopLength; i++) {
    // 1. Loop to find the first difference between words and editedWords
    if (editedWords[i] === words[i]?.value) {
      continue;
    }

    // 2. Modify/return a new words array based on which edit scenario occurred

    // 2a. word(s) added at the end
    if (i >= words.length) {
      return insertManyAt(
        words,
        editedWords
          .slice(i, i + wordsAdded)
          .map((value) => ({ id: getTemporaryId(), value })),
        i
      );
    }

    // 2b. word(s) removed at the end
    if (i >= editedWords.length) {
      return removeManyAt(words, i, wordsRemoved);
    }

    let newWords: TranscriptWord[] = [...words];

    // 2c. word(s) added in the middle
    if (wordsAdded > 0) {
      // an existing word was split
      if (
        i < editedWords.length - 1 &&
        editedWords[i] + editedWords[i + 1] === words[i].value
      ) {
        // insert first half as a new word
        newWords = insertAt(
          newWords,
          { id: getTemporaryId(), value: editedWords[i] },
          i
        );

        // replace existing word with second half
        newWords = replaceAt(
          newWords,
          { ...words[i], value: editedWords[i + 1] },
          i + 1
        );

        return newWords;
      }

      // find the range of words to insert
      let j = i;

      while (j < loopLength && editedWords[j] !== words[i]?.value) {
        j++;
      }

      newWords = insertManyAt(
        newWords,
        editedWords
          .slice(i, j)
          .map((value) => ({ id: getTemporaryId(), value })),
        i
      );

      return newWords.slice(0, editedWords.length);
    }

    // 2d. word(s) removed in the middle
    if (wordsRemoved > 0) {
      newWords = removeManyAt(newWords, i, wordsRemoved);

      const oldValue = words[i].value + words[i + 1].value;

      // an existing word was combined into one
      if (i < newWords.length - 1 && oldValue === editedWords[i]) {
        newWords = replaceAt(
          newWords,
          {
            value: oldValue,
            id: getTemporaryId(),
            startTime: words[i].startTime,
            endTime: words[i + 1].endTime,
          },
          i
        );
      }
    }

    // 3. Finally update any existing word(s) that were changed
    let j = i;

    while (j < loopLength && editedWords[j] !== newWords[j]?.value) {
      newWords = replaceAt(
        newWords,
        { ...newWords[j], value: editedWords[j] },
        j
      );
      j++;
    }

    return newWords;
  }

  // nothing changed
  return words;
};

/** This is a naive method that handles simply splitting an array of word
 * based on the text passed. Assumes that all words in the text are present
 * in the words array and in the same order as they appear in the text.
 * Handles if a word was split in half */
export const splitWords = ({
  words,
  text,
  wordDelimiter,
  direction = "default",
}: {
  words: TranscriptWord[];
  text: string;
  wordDelimiter: WordDelimiter;
  direction?: "default" | "reverse";
}) => {
  const rawWordsFromText = map(trim, compact(trim(text).split(wordDelimiter)));

  if (isEmpty(rawWordsFromText)) return [];

  const wordSubset: TranscriptWord[] = [];
  const shouldGoInReverse = direction === "reverse";

  const wordsListToCompare = shouldGoInReverse ? reverse(words) : words;
  const rawWordsToCompare = shouldGoInReverse
    ? reverse(rawWordsFromText)
    : rawWordsFromText;

  for (let i = 0; i < rawWordsFromText.length; i++) {
    const wordFromText = rawWordsToCompare[i];
    const wordFromList = wordsListToCompare[i];
    if (wordFromList.value === wordFromText) {
      wordSubset.push(wordFromList);
    } else {
      wordSubset.push({ id: getTemporaryId(), value: wordFromText });
    }
  }

  return shouldGoInReverse ? reverse(wordSubset) : wordSubset;
};

/** This function strips the id field, which isn't necessary to send in an add/update API operation */
const generateApiTranscriptOperationWords = (
  words: ApiTranscriptWord[]
): ApiTranscriptOperationWord[] =>
  words.map(({ value, startTime, endTime }) => ({ value, startTime, endTime }));

const generateTranscriptOperation = (
  edit: SingleTranscriptEdit
): ApiTranscriptOperation | null => {
  const beforeSpeakerName = edit.before ? edit.before.turn.speaker : null;
  const afterSpeakerName = edit.after.turn.speaker ?? UNKNOWN_SPEAKER;

  // don't tell the BE that the speaker changed just because we show a different
  // speaker display name in the UI
  const isUpdatedSpeakerSuppressed =
    SUPPRESSED_SPEAKER_NAMES.includes(afterSpeakerName);

  if (!edit.before) {
    const createOperation: ApiCreateTranscriptTurnOperation = {
      operationKind: "create_turn",
      id: makeIdFromTemporaryId(edit.after.turn.id)!,
      parentId: makeIdFromTemporaryId(edit.after.turn.previousTurnId),
      speaker: { name: afterSpeakerName },
      words: generateApiTranscriptOperationWords(edit.after.words),
    };
    return createOperation;
  }

  if (edit.after.isDeleted) {
    const deleteOperation: ApiDeleteTurnOperation = {
      operationKind: "delete_turn",
      turnId: edit.after.turn.id,
    };
    return deleteOperation;
  }

  const includeWords = edit.before.text !== edit.after.text;
  const includeSpeaker =
    !isUpdatedSpeakerSuppressed && beforeSpeakerName !== afterSpeakerName;
  if (!includeWords && !includeSpeaker) return null;

  const updateOperation: ApiUpdateTranscriptTurnOperation = {
    operationKind: "update_turn",
    turnId: edit.after.turn.id,
    speaker: includeSpeaker ? { name: afterSpeakerName } : undefined,
    words: includeWords
      ? generateApiTranscriptOperationWords(edit.after.words)
      : undefined,
  };
  return updateOperation;
};

export const generateTranscriptApiOperationsFromEdits = (
  edits: SingleTranscriptEdit[]
): ApiTranscriptOperation[] => {
  return compact(map(generateTranscriptOperation, edits));
};

const generateSessionNoteOperation = (
  edit: SingleSessionNoteEdit
): ApiSessionNoteOperation => {
  if (!edit.before) {
    const createOperation: ApiCreateSessionNoteOperation = {
      operation: "create",
      sessionId: edit.after.sessionId,
      text: edit.after.value,
      private: true,
    };
    return createOperation;
  }

  if (edit.after.isDeleted) {
    const deleteOperation: ApiDeleteSessionNoteOperation = {
      operation: "delete",
      id: edit.after.id,
    };
    return deleteOperation;
  }

  const updateOperation: ApiUpdateSessionNoteOperation = {
    operation: "update",
    id: edit.after.id,
    text: edit.after.value,
  };
  return updateOperation;
};

const generateClipOperation = (
  edit: SingleSummaryItemEdit | SessionChapterEdit,
  sessionId: string
): ApiClipOperation | null => {
  if (!edit.before) {
    // we do not support creating chapter edits, but to appease the types
    if (isChapterEdit(edit)) return null;

    const createOperation: ApiCreateClipOperation = {
      operation: "create",
      clipType: edit.type as CreatableClipType,
      sessionId,
      text: edit.after.value,
    };
    return createOperation;
  }

  if ("isDeleted" in edit.after && edit.after.isDeleted) {
    const deleteOperation: ApiDeleteClipOperation = {
      operation: "delete",
      id: edit.after.id,
      clipType: edit.type as ApiDeleteClipOperation["clipType"],
      sessionId,
    };
    return deleteOperation;
  }

  const updateOperation: ApiUpdateClipOperation = {
    operation: "update",
    id: edit.after.id,
    clipType: edit.type,
    sessionId,
    ...(isChapterEdit(edit)
      ? {
          secondaryText: edit.after.description,
          text: edit.after.title,
        }
      : { text: edit.after.value }),
  };
  return updateOperation;
};

export const generateNoteApiOperationsFromEdits = (
  edits: SingleNoteEdit[],
  sessionId: string
) => {
  const [noteEdits, clipEdits] = partition(isSessionNoteEdit, edits);

  const noteOperations = map(
    generateSessionNoteOperation,
    noteEdits as SingleSessionNoteEdit[]
  );
  const clipOperations = compact(
    map(
      (edit) => generateClipOperation(edit, sessionId),
      clipEdits as (SingleSummaryItemEdit | SessionChapterEdit)[]
    )
  );

  return {
    noteOperations,
    clipOperations,
  };
};

/**
 * Takes a transcript turn, a list of search results, and a replacement string
 * and returns a new turn will all instances of the search results replaced
 * with the replacement string
 */
export const replaceAllInTurn = (
  turn: TranscriptTurnParagraph,
  searchResults: TranscriptSearchResult[],
  replacement: string,
  wordDelimiter: WordDelimiter
): TranscriptTurnParagraph => {
  const turnChunks = chunkContentBySearchResults(
    turn.words,
    searchResults,
    wordDelimiter
  );
  if (isEmpty(turnChunks)) return turn;

  // we will modify the chunks while reducing the original turn chunks;
  // we need this copy so we can apply multiple replace operations
  // to the same turn, one at a time since getEditedWords expects a single
  // contiguous change.
  let turnChunksAcc = [...turnChunks];

  const editedWords = turnChunks.reduce((words, chunk, i) => {
    if (chunk.highlighted) {
      const newChunk: TranscriptTextChunk = {
        ...chunk,
        highlighted: false,
        content: replacement,
      };

      // replace the chunk with our new replacement value chunk
      // and then build the full updated text
      turnChunksAcc = replaceAt(turnChunksAcc, newChunk, i);
      const updatedText = join("", map("content", turnChunksAcc));
      // return the updated word list from the updated text
      return getEditedWords(words, updatedText, wordDelimiter);
    }
    return words;
  }, turn.words);

  return {
    ...turn,
    words: editedWords,
    text: join(wordDelimiter, map("value", editedWords)),
  };
};

export const replaceAllInTranscript = ({
  replacementValue,
  searchResults,
  turns,
  wordDelimiter,
}: {
  replacementValue: string;
  searchResults: Map<string, TranscriptSearchResult[]>;
  turns: TranscriptTurnParagraph[];
  wordDelimiter: WordDelimiter;
}): SingleTranscriptEdit[] => {
  const turnsWithSearchResults = filter(
    (turn) => searchResults.has(turn.id),
    turns
  );

  const edits = map((turn) => {
    const searchResultsForTurn = searchResults.get(turn.id) ?? [];
    const afterTurn = replaceAllInTurn(
      turn,
      searchResultsForTurn,
      replacementValue,
      wordDelimiter
    );

    const edit: SingleTranscriptEdit = {
      type: "turn",
      before: turn,
      after: afterTurn,
    };
    return edit;
  }, turnsWithSearchResults);

  return edits;
};

export const replaceAllInNoteItem = <T extends NoteItemOrChapter>(
  item: T,
  searchResults: NoteSearchResult[],
  replacement: string
): T => {
  if (isChapter(item)) {
    const [titleSearchResults, descriptionSearchResults] = partition(
      (result) => result.note.id === getChapterTitleSearchResultKey(item),
      searchResults
    );

    let updatedTitle = item.title;
    let updatedDescription = item.description;

    if (titleSearchResults.length) {
      const titleChunks = chunkNoteBySearchResults(
        item.title,
        titleSearchResults
      );
      updatedTitle = titleChunks
        .map((chunk) => (chunk.highlighted ? replacement : chunk.content))
        .join("");
    }

    if (descriptionSearchResults.length) {
      const descriptionChunks = chunkNoteBySearchResults(
        item.description,
        descriptionSearchResults
      );
      updatedDescription = descriptionChunks
        .map((chunk) => (chunk.highlighted ? replacement : chunk.content))
        .join("");
    }

    return {
      ...item,
      title: updatedTitle,
      description: updatedDescription,
    };
  }

  const noteChunks = chunkNoteBySearchResults(item.value, searchResults);
  if (isEmpty(noteChunks)) return item;

  let updatedValue = "";

  noteChunks.forEach((chunk) => {
    updatedValue += chunk.highlighted ? replacement : chunk.content;
  });

  return {
    ...item,
    value: updatedValue,
  };
};

const getSearchResultForItem = (
  item: NoteItemOrChapter,
  results: Map<string, NoteSearchResult[]>
) => {
  if (isChapter(item)) {
    const titleResults = results.get(getChapterTitleSearchResultKey(item));
    const descriptionResults = results.get(
      getChapterDescriptionSearchResultKey(item)
    );
    return flatten(compact([titleResults, descriptionResults]));
  }
  return results.get(item.id) ?? [];
};

const itemHasSearchResults = (
  item: NoteItemOrChapter,
  results: Map<string, NoteSearchResult[]>
) => {
  if (isChapter(item)) {
    return (
      results.has(getChapterTitleSearchResultKey(item)) ||
      results.has(getChapterDescriptionSearchResultKey(item))
    );
  }

  return results.has(item.id);
};

export const replaceAllInNotes = ({
  replacementValue,
  searchResults,
  getNoteItemsByType,
}: {
  replacementValue: string;
  searchResults: NoteSearchResultMap;
  getNoteItemsByType: (type: NoteItemOrChapterType) => NoteItemOrChapter[];
}): SingleNoteEdit[] => {
  const edits = Object.entries(searchResults).map(([type, resultsById]) => {
    const itemsOfTypeWithSearchResults = filter(
      (item) => itemHasSearchResults(item, resultsById),
      getNoteItemsByType(type as NoteItemOrChapterType)
    );

    return itemsOfTypeWithSearchResults.map((item) => {
      const resultsForItem = getSearchResultForItem(item, resultsById);
      const after = replaceAllInNoteItem(
        item,
        resultsForItem,
        replacementValue
      );

      const edit = {
        type,
        before: item,
        after,
      } as SingleNoteEdit;

      return edit;
    });
  });

  return flatten(edits);
};

interface ReplaceOrRemoveOpts<T> {
  replacement?: T;
  itemToReplace: T;
  list: T[];
}

interface ReplaceOrAddOpts<T> {
  replacement: T;
  itemToReplace?: T;
  list: T[];
}

// either `replacement` or `itemToReplace` can be undefined, but not both
type ReplaceItemInListOpts<T> = ReplaceOrRemoveOpts<T> | ReplaceOrAddOpts<T>;

export const replaceItemInList = <T extends { id: string }>({
  replacement,
  itemToReplace,
  list,
}: ReplaceItemInListOpts<T>): T[] => {
  if (itemToReplace) {
    return compact(
      replaceWhere(list, replacement, (item) => item?.id === itemToReplace.id)
    );
  }

  return compact([...list, replacement]);
};
